import { UserCoverageSummary } from '@ampsec/platform-client';
import Laptop from 'Assets/icons/laptop.svg';
import Mobile from 'Assets/icons/mobile.svg';
import classNames from 'classnames';
import { ToolingCoverageTooltip } from 'Components/molecules/ToolingCoverageTooltip';
import { getOsIcon, getUserAssetStatus } from '../ToolingComplianceComponentHelper';
import { GridContext, AssetsType } from '../AgGridCompliance.config';
import { OsType } from 'Core-utils/types';
import { DateTimeFormatter } from 'Core-utils/helpers/helpers';
import { FaQuestionCircle } from '@react-icons/all-files/fa/FaQuestionCircle';
import IconWithStatus from 'Components/molecules/IconWithStatus/IconWithStatus';
import { formatCsvTooltipGroups, type CsvTooltipField, type CsvTooltipGroup } from '../../AgGridBase/exportCsv';

export type AssetTooltipField = 'lastCheckin' | 'status' | 'hostname' | 'serialNumber' | 'os' | 'model';

export const DEFAULT_ASSET_TOOLTIP_FIELDS: AssetTooltipField[] = [
  'lastCheckin',
  'status',
  'hostname',
  'serialNumber',
  'os',
  'model',
];

export const MISSING_ASSET_TIP_KEY = 'TOOLING_COMPLIANCE_USER_MISSING_ASSET_TIP';
export const MISSING_ASSET_LABEL_KEY = 'TOOLING_COMPLIANCE_USER_MISSING_ASSET_LABEL';

/** A tooltip row of an asset: which field it shows, plus the label/value/status as rendered. */
export type AssetTooltipRow = CsvTooltipField & {
  field: AssetTooltipField;
  value: string;
  status?: 'warn' | 'error';
};

/**
 * The tooltip rows of one asset, in render order, using the same formatting the tooltip shows
 * (formatted dates, ACTIVE / INACTIVE, NEVER). Both the cell renderer and the CSV export read from
 * here so the exported values cannot drift from what the UI renders.
 */
export function getAssetTooltipFields(asset: AssetsType | undefined): AssetTooltipRow[] {
  const computedStatus = getUserAssetStatus(Boolean(asset?.isAssetActive), asset?.lastCheckinTs);
  const rowStatus = computedStatus !== 'active' ? computedStatus : undefined;
  return [
    {
      field: 'lastCheckin',
      label: 'Last Check in',
      value: asset?.lastCheckinTs ? DateTimeFormatter(asset.lastCheckinTs) : 'NEVER',
      status: rowStatus,
    },
    {
      field: 'status',
      label: 'Status',
      value: asset?.isAssetActive ? 'ACTIVE' : 'INACTIVE',
      status: rowStatus,
    },
    { field: 'hostname', label: 'Host Name', value: asset?.hostname ?? '' },
    { field: 'serialNumber', label: 'Serial Number', value: asset?.sn ?? '' },
    { field: 'os', label: 'OS Version', value: asset?.os ?? '' },
    { field: 'model', label: 'Model', value: asset?.model ?? '' },
  ];
}

/**
 * The CSV tooltip cell of the assets column: one group per asset row the cell renders (the asset's
 * display value followed by its tooltip rows), and the missing-asset state when the cell renders
 * only the question-mark indicator.
 */
export function getAssetTooltipExport(row: UserCoverageSummary, context: GridContext): string {
  const groups: CsvTooltipGroup[] = (row.assets ?? []).map((asset) => ({
    label: asset?.displayValue ?? 'Asset',
    fields: getAssetTooltipFields(asset).filter(({ field }) =>
      DEFAULT_ASSET_TOOLTIP_FIELDS.includes(field)
    ),
  }));

  if (groups.length === 0) {
    groups.push({ label: context.t(MISSING_ASSET_TIP_KEY), fields: [] });
  }

  return formatCsvTooltipGroups(groups);
}

interface AssetsCellRendererProps {
  data: UserCoverageSummary;
  context: GridContext;
  tooltipFields?: AssetTooltipField[];
}

interface AssetRowProps {
  asset: AssetsType;
  imageSrc: string;
  isLast: boolean;
  isCompact?: boolean;
  tooltipFields: AssetTooltipField[];
}
interface MissingAssetIndicatorProps {
  context: GridContext;
}

const MissingAssetIndicator = ({ context }: MissingAssetIndicatorProps) => {
  return (
    <ToolingCoverageTooltip>
      <ToolingCoverageTooltip.Trigger>
        <div className='coverage-cell__asset-row'>
          <FaQuestionCircle className="text-text-low-emphasis align-items-center" size={20} style={{ opacity: 0.5, display: 'block' }} />
          <span className="text-text-low-emphasis align-items-center">{context.t('TOOLING_COMPLIANCE_USER_MISSING_ASSET_LABEL')}</span>
        </div>
      </ToolingCoverageTooltip.Trigger>

      <ToolingCoverageTooltip.Header
        icon={<FaQuestionCircle className="text-text-low-emphasis align-items-center" size={20} style={{ display: 'block' }} />}
        label={context.t('TOOLING_COMPLIANCE_USER_MISSING_ASSET_TIP')}
      />
    </ToolingCoverageTooltip>
  );
};

function getDeviceImageSrc(asset: AssetsType, mobileCategoriesArray: string[]): string {
  const assetString = `${asset?.os || ''}${asset?.model || ''}`.toLowerCase();
  const isMobile = mobileCategoriesArray.some((pattern) => new RegExp(pattern, 'i').test(assetString));
  return isMobile ? Mobile : Laptop;
}

const AssetRow = ({ asset, imageSrc, isLast, isCompact = false, tooltipFields }: AssetRowProps) => {
  const computedStatus = getUserAssetStatus(asset?.isAssetActive, asset?.lastCheckinTs);
  return (
  <ToolingCoverageTooltip>
    <ToolingCoverageTooltip.Trigger>
      <div className={classNames(
        'coverage-cell__asset-row',
        { 'mb-1': !isLast },
        { 'coverage-cell__asset-row--compact': isCompact }
      )}>
        {asset?.displayValue && <IconWithStatus status={computedStatus} icon={<img src={imageSrc} width={isCompact ? 18 : 25} height={isCompact ? 18 : 25} alt="" />} />}
        <span className={classNames({ 'coverage-cell__asset-label--compact': isCompact })}>
          {asset?.displayValue}
        </span>
      </div>
    </ToolingCoverageTooltip.Trigger>

    <ToolingCoverageTooltip.Header
      icon={getOsIcon((asset?.os as OsType) ?? '', computedStatus)}
      label={asset?.displayValue}
    />
    {getAssetTooltipFields(asset)
      .filter((row) => tooltipFields.includes(row.field))
      .map((row) => (
        <ToolingCoverageTooltip.Row key={row.field} label={row.label} value={row.value} status={row.status} />
      ))}
  </ToolingCoverageTooltip>
  );
};

const AssetsCellRenderer = ({ data: row, context, tooltipFields = DEFAULT_ASSET_TOOLTIP_FIELDS }: AssetsCellRendererProps) => {
  const mobileCategoriesArray: string[] = context.t('__mobile-categories')
    .split(',')
    .map((category: string) => category.toLowerCase());
  const isCompact = row.assets.length > 1;
  return (
    <div
      className={classNames('coverage-cell coverage-cell--column', {
        'coverage-cell--transparent': !row.assets[0]?.isAssetActive,
        'coverage-cell--compact': isCompact,
      })}
    >
      {row.assets.length === 0 && (
        <div className="coverage-cell__asset-row">
          <MissingAssetIndicator context={context} />
        </div>
      )}
      {row.assets.map((asset, idx) => (
        <AssetRow
          key={asset.id}
          asset={asset}
          imageSrc={getDeviceImageSrc(asset, mobileCategoriesArray)}
          isLast={idx === row.assets.length - 1}
          isCompact
          tooltipFields={tooltipFields}
        />
      ))}
    </div>
  );
};

export default AssetsCellRenderer;
